import { useRef, useState, useEffect } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Search, ShieldAlert, LogOut, Home, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import { TasksBadge } from "@/components/TasksBadge";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiGetCompanyLogo, apiGetTenantConfig, apiGetNavPermissions, getTenantSlug, API_BASE } from "@/lib/api";
import { AIAssistantSheet } from "@/components/ai/AIAssistantSheet";
import { useAiFabEnabled } from "@/hooks/useAiFabEnabled";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { format } from "date-fns";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const pageTitleMap: Record<string, string> = {
    "/": t("nav.dashboard", "Dashboard"),
    "/hr-dashboard": t("nav.hrDashboard", "HR Dashboard"),
    "/employees": t("nav.peopleDirectory", "People Directory"),
    "/my-profile": t("nav.myProfile", "My Profile"),
    "/my-salary": t("nav.salary", "My Salary"),
    "/my-tasks": t("nav.myTasks", "My Tasks"),
    "/my-onboarding": t("nav.myOnboarding", "My Onboarding"),
    "/my-expenses": t("nav.myExpenses", "My Expenses"),
    "/my-documents": t("nav.myDocuments", "My Documents"),
    "/messages": t("nav.messages", "Messages"),
    "/onboarding": t("nav.onboarding", "Workforce Lifecycle"),
    "/offboarding": t("nav.offboarding", "Offboarding"),
    "/leave": t("nav.leaveManagement", "Leave Management"),
    "/team-leave": t("nav.teamLeave", "Team Leave"),
    "/performance": t("nav.careerDevelopment", "Career Development"),
    "/payroll": t("nav.payroll", "Payroll"),
    "/organization": t("nav.organization", "Organization"),
    "/team-overview": t("nav.teamOverview", "Team Overview"),
    "/surveys": t("nav.surveys", "Surveys"),
    "/my-surveys": t("nav.mySurveys", "My Surveys"),
    "/workflows": t("nav.workflows", "Workflows"),
    "/documents": t("nav.hrDocuments", "Documents"),
    "/corporate-calendar": t("nav.corporateCalendar", "Corporate Calendar"),
    "/corporate-events": t("nav.corporateEvents", "Corporate Events"),
    "/notifications": t("nav.notifications", "Notifications"),
    "/settings": t("nav.settings", "Settings"),
    "/field-permissions": t("nav.fieldPermissions", "Field Permissions"),
    "/nav-permissions": t("nav.navPermissions", "Nav Permissions"),
    "/recruitment": t("nav.recruitment", "Recruitment"),
    "/equipment": t("nav.equipmentManagement", "Equipment"),
    "/medical-checkups": t("nav.medicalCheckups", "Medical Checkups"),
    "/expenses": t("nav.expenses", "Expenses"),
    "/hr-analytics": t("nav.hrAnalytics", "HR Analytics"),
    "/certifications": t("nav.certifications", "Certifications"),
    "/audit-log": t("nav.auditLog", "Audit Log"),
    "/gdpr-retention": t("nav.gdprRetention", "GDPR Retention"),
    "/hr-cases": t("nav.hrOpenCases", "HR Open Cases"),
    "/raise-concern": t("nav.raiseConcern", "Raise Concern"),
    "/sara-help": t("nav.saraHelp", "Sara Help"),
    "/ai-assistant": "Sara AI",
  };
  const pageTitle = pageTitleMap[location.pathname] ?? pageTitleMap[Object.keys(pageTitleMap).find(k => location.pathname.startsWith(k) && k !== "/") ?? ""] ?? t("nav.dashboard", "Dashboard");

  // ── Breadcrumbs ──────────────────────────────────────────────────────────
  // Generic label for the trailing crumb of a dynamic detail route (e.g.
  // /employees/:id, /corporate-calendar/:id), keyed by the matched section prefix.
  const detailLabelMap: Record<string, string> = {
    "/employees": t("breadcrumb.employeeProfile", "Employee Profile"),
    "/corporate-calendar": t("breadcrumb.eventDetails", "Event Details"),
  };
  const matchedPrefix = Object.keys(pageTitleMap)
    .filter((k) => k !== "/" && location.pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  const breadcrumbItems: { label: string; href?: string }[] = [
    { label: t("nav.dashboard", "Dashboard"), href: "/" },
  ];
  if (matchedPrefix) {
    const isCurrent = location.pathname === matchedPrefix;
    breadcrumbItems.push({ label: pageTitleMap[matchedPrefix], href: isCurrent ? undefined : matchedPrefix });
    if (!isCurrent) {
      breadcrumbItems.push({ label: detailLabelMap[matchedPrefix] ?? t("breadcrumb.details", "Details") });
    }
  }
  // Avoid a redundant "Dashboard / Dashboard" crumb on the home/HR dashboard.
  const breadcrumbs = breadcrumbItems.length > 1 ? breadcrumbItems : [{ label: breadcrumbItems[0].label }];

  const { data: logoUrl } = useQuery({
    queryKey: ["company-logo", getTenantSlug()],
    queryFn: apiGetCompanyLogo,
    staleTime: 5 * 60 * 1000,
  });

  const { data: tenantConfig } = useQuery({
    queryKey: ["tenant-config"],
    queryFn: apiGetTenantConfig,
    staleTime: 10 * 60 * 1000,
  });

  const { data: navPermissions } = useQuery({
    queryKey: ["nav-permissions"],
    queryFn: apiGetNavPermissions,
    staleTime: 5 * 60 * 1000,
  });

  // Show FAB only when: not on the full-screen AI page AND aiAssistant is available at plan level AND not restricted for this role
  const aiModuleOn = !tenantConfig?.enabledModules || tenantConfig.enabledModules["aiAssistant"] !== false;
  const aiNavSetting = navPermissions?.find((p) => p.navKey === "aiAssistant");
  const roleKey = user?.role?.mainRole ?? "user";
  const roleNavFieldMap: Record<string, keyof NonNullable<typeof aiNavSetting>> = {
    admin:        "visibleToAdmin",
    hr:           "visibleToHr",
    user:         "visibleToUser",
    line_manager: "visibleToLineManager",
    c_level:      "visibleToCLevel",
  };
  const navField = roleNavFieldMap[roleKey] ?? "visibleToUser";
  const aiRoleVisible = !aiNavSetting || aiNavSetting[navField] !== false;
  const aiFabEnabled = useAiFabEnabled();
  const showFAB = location.pathname !== "/ai-assistant" && aiModuleOn && aiRoleVisible && aiFabEnabled;

  // ── Superadmin impersonation banner ─────────────────────────────────────────
  // Read the active impersonation session from localStorage (set by the
  // superadmin panel when "View as role" is started). Shown as a fixed amber
  // banner with a countdown and an exit button. No context import needed —
  // we read/write localStorage directly to avoid coupling the normal app shell
  // to the superadmin context.
  const [impSession, setImpSession] = useState<{
    role: string; tenantName: string; expiresAt: string;
    sessionId: number; accountId: number;
  } | null>(() => {
    try {
      const raw = localStorage.getItem("besthr_sa_impersonation");
      if (!raw) return null;
      const p = JSON.parse(raw);
      return new Date(p.expiresAt) > new Date() ? p : null;
    } catch { return null; }
  });

  const [minsLeft, setMinsLeft] = useState<number>(() =>
    impSession
      ? Math.max(0, Math.ceil((new Date(impSession.expiresAt).getTime() - Date.now()) / 60_000))
      : 0
  );

  useEffect(() => {
    if (!impSession) return;
    const tick = () => {
      const left = Math.ceil((new Date(impSession.expiresAt).getTime() - Date.now()) / 60_000);
      if (left <= 0) {
        const slug = (impSession as any).tenantSlug;
        if (slug) {
          localStorage.removeItem(`besthr_${slug}_token`);
          localStorage.removeItem(`besthr_${slug}_user`);
        }
        localStorage.removeItem("besthr_sa_impersonation");
        localStorage.removeItem("besthr_active_tenant");
        // Hard redirect — setImpSession(null) alone leaves AuthContext.user set
        // to the ghost user in React state, so RequireAuth still passes and all
        // subsequent API calls fire with _t=public + no JWT → 401.
        window.location.replace("/superadmin");
      } else {
        setMinsLeft(left);
      }
    };
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [impSession]);

  const handleExitImpersonation = () => {
    const saToken = localStorage.getItem("besthr_sa_token");
    if (impSession && saToken) {
      fetch(
        `${API_BASE}/api/superadmin/accounts/${impSession.accountId}/impersonate/${impSession.sessionId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${saToken}` } },
      ).catch(() => {});
      // Remove the injected tenant credentials
      const slug = (impSession as any).tenantSlug;
      if (slug) {
        localStorage.removeItem(`besthr_${slug}_token`);
        localStorage.removeItem(`besthr_${slug}_user`);
      }
    }
    localStorage.removeItem("besthr_sa_impersonation");
    localStorage.removeItem("besthr_active_tenant");
    window.location.href = "/superadmin";
  };
  // ── End impersonation banner ─────────────────────────────────────────────────

  function handleScroll() {
    setScrolled((mainRef.current?.scrollTop ?? 0) > 4);
  }

  return (
    <>
      {/* Superadmin impersonation banner — fixed, sits above everything */}
      {impSession && (
        <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between px-4 py-2 bg-amber-400 text-amber-950 text-sm font-medium shadow-lg">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 shrink-0" />
            <span>
              Superadmin preview — viewing as{" "}
              <strong className="font-semibold">{impSession.role.replace("_", " ")}</strong>
              {" "}in <strong className="font-semibold">{impSession.tenantName}</strong>
            </span>
            <span className="text-amber-800 text-xs ml-1">
              ({minsLeft} min remaining · read-only)
            </span>
          </div>
          <button
            onClick={handleExitImpersonation}
            className="flex items-center gap-1.5 px-3 py-1 rounded bg-amber-950/15 hover:bg-amber-950/25 text-amber-950 text-xs font-semibold transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit Preview
          </button>
        </div>
      )}
    <SidebarProvider>
      <div className={`h-screen flex w-full overflow-hidden${impSession ? " pt-10" : ""}`}>
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <header
            className={`h-14 border-b border-border flex items-center justify-between px-4 bg-card shrink-0 sticky top-0 z-40 transition-shadow duration-200${
              scrolled ? " shadow-md" : ""
            }`}
          >
            <div className="flex items-center gap-3">
              <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
              {/* Morphing search — circle expands into input bar */}
              <div
                className="relative flex items-center transition-all duration-300 ease-in-out rounded-full border cursor-text"
                style={{
                  width: searchOpen ? "220px" : "36px",
                  height: "36px",
                  borderColor: searchOpen ? "hsl(var(--primary))" : "hsl(var(--border))",
                  backgroundColor: searchOpen ? "hsl(var(--background))" : "transparent",
                  boxShadow: searchOpen ? "0 0 0 3px hsl(var(--primary) / 0.15)" : "none",
                }}
                onClick={() => !searchOpen && setSearchOpen(true)}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setSearchOpen((v) => !v); }}
                  className="absolute left-0 flex items-center justify-center shrink-0 transition-colors duration-200 rounded-full"
                  style={{ width: "36px", height: "36px", color: searchOpen ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))" }}
                  aria-label="Search"
                >
                  <Search className="h-4 w-4" />
                </button>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t("common.search")}
                  className="bg-transparent text-sm outline-none placeholder:text-muted-foreground text-foreground transition-all duration-300"
                  style={{
                    width: searchOpen ? "calc(220px - 44px)" : "0px",
                    opacity: searchOpen ? 1 : 0,
                    paddingLeft: searchOpen ? "40px" : "0px",
                    pointerEvents: searchOpen ? "auto" : "none",
                  }}
                  onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
                  onBlur={() => setSearchOpen(false)}
                />
              </div>
            </div>

            {/* Centre: page title + date */}
            <div className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none select-none hidden sm:block">
              <p className="text-[11px] text-muted-foreground leading-none mb-0.5">{format(new Date(), "EEEE d MMMM")}</p>
              <p className="text-sm font-semibold text-foreground leading-none">{pageTitle}</p>
            </div>

            <div className="flex items-center gap-3">
              <span data-tour="header-tasks"><TasksBadge /></span>
              <span data-tour="header-notifications"><NotificationBell /></span>
              {/* Tenant slug — only shown when no org logo is uploaded */}
              {!logoUrl && (
                <span className="text-[10px] font-mono px-2 py-1 rounded bg-primary/10 text-primary border border-primary/20 font-semibold uppercase tracking-wider">
                  {getTenantSlug()}
                </span>
              )}
            </div>
          </header>

          {/* Breadcrumbs */}
          <div className="h-9 border-b border-border flex items-center gap-1.5 px-4 bg-card/50 shrink-0 text-xs text-muted-foreground overflow-x-auto">
            <Home className="h-3.5 w-3.5 shrink-0" />
            {breadcrumbs.map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 whitespace-nowrap">
                {i > 0 && <ChevronRight className="h-3 w-3 shrink-0" />}
                {item.href ? (
                  <Link to={item.href} className="hover:text-foreground transition-colors">
                    {item.label}
                  </Link>
                ) : (
                  <span className="text-foreground font-medium">{item.label}</span>
                )}
              </span>
            ))}
          </div>

          <main
            ref={mainRef}
            onScroll={handleScroll}
            data-tour="page-main"
            className="flex-1 overflow-auto p-6 pb-28 bg-background"
          >
            {impSession ? (
              <div className="imp-readonly-zone">{children}</div>
            ) : (
              children
            )}
          </main>

          {/* Global CSS: disable all form controls inside the main content during impersonation */}
          {impSession && (
            <style>{`
              .imp-readonly-zone button,
              .imp-readonly-zone [role="button"],
              .imp-readonly-zone input,
              .imp-readonly-zone textarea,
              .imp-readonly-zone select,
              .imp-readonly-zone [contenteditable="true"] {
                pointer-events: none !important;
                opacity: 0.55 !important;
                cursor: not-allowed !important;
                user-select: none !important;
              }
              /* Keep navigation links and tab triggers clickable */
              .imp-readonly-zone a,
              .imp-readonly-zone [role="tab"],
              .imp-readonly-zone [data-state],
              .imp-readonly-zone nav button,
              .imp-readonly-zone [role="tablist"] button {
                pointer-events: auto !important;
                opacity: 1 !important;
                cursor: pointer !important;
              }
            `}</style>
          )}

          {/* Floating AI Assistant button — hidden on the full-screen AI page */}
          {showFAB && <AIAssistantSheet />}

          {/* Footer */}
          <footer className="shrink-0 border-t border-border bg-card px-6 py-2 flex items-center justify-center gap-2">
            <img src="/besthr.png" alt="BestHR" className="h-5 w-5 object-contain opacity-80" />
            <p className="text-xs text-muted-foreground">
              {t("common.platformFooter")}
            </p>
          </footer>
        </div>
      </div>
    </SidebarProvider>
  </>
  );
}
